import { useEffect, useState } from 'react'
import { Empty, Spin, Tag, Button } from 'antd'
import { DownloadOutlined, FileTextOutlined } from '@ant-design/icons'
import { getStockReports, getStockReportDetail } from '@/api/stock'
import type { StockReport, StockReportDetail } from '@/types/stockPool'
import { useFeedback } from '@/utils/antdApp'
import { MdViewer } from '@/components/MarkdownFile'
import './index.scss'

interface Props {
    stockCode: string
    onCountChange?: (count: number) => void
}

export default function HistoryAnalysis({ stockCode, onCountChange }: Props) {
    const [reports, setReports] = useState<StockReport[]>([])
    const [loading, setLoading] = useState(true)
    const [expandedId, setExpandedId] = useState<number | null>(null)
    const [detail, setDetail] = useState<StockReportDetail | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const { message } = useFeedback()

    useEffect(() => {
        if (!stockCode) return
        let cancelled = false
        setLoading(true)
        setExpandedId(null)
        setDetail(null)
        getStockReports({ code: stockCode, report_type: 'research', limit: 50 })
            .then((data) => {
                if (cancelled) return
                const items = data.items ?? []
                setReports(items)
                onCountChange?.(data.total ?? items.length)
            })
            .catch(() => {
                if (cancelled) return
                setReports([])
                onCountChange?.(0)
                message.error('获取历史分析研报失败')
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => { cancelled = true }
    }, [stockCode])

    const handleToggle = async (report: StockReport) => {
        if (expandedId === report.id) {
            setExpandedId(null)
            setDetail(null)
            return
        }
        setExpandedId(report.id)
        setDetailLoading(true)
        setDetail(null)
        try {
            const data = await getStockReportDetail({ id: report.id })
            setDetail(data)
        } catch {
            message.error('获取研报详情失败')
        } finally {
            setDetailLoading(false)
        }
    }

    const handleDownload = (url: string) => {
        window.open(url, '_blank')
    }

    if (loading) {
        return (
            <div className="history-analysis history-analysis--center">
                <Spin />
            </div>
        )
    }

    if (!reports.length) {
        return (
            <div className="history-analysis history-analysis--center">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无研报分析" />
            </div>
        )
    }

    return (
        <div className="history-analysis">
            <div className="history-analysis__header">
                <span className="history-analysis__header-title">历史分析</span>
                <span className="history-analysis__header-count">{reports.length} 篇研报</span>
            </div>
            <div className="history-analysis__body">
                {reports.map((item) => {
                    const isOpen = expandedId === item.id
                    const downloadUrl = isOpen
                        ? (detail?.source_url || detail?.pdf_url || item.source_url || null)
                        : (item.source_url || null)
                    return (
                        <div
                            className={`history-analysis__item ${isOpen ? 'is-open' : ''}`}
                            key={item.id}
                        >
                            <div
                                className="history-analysis__item-main"
                                onClick={() => handleToggle(item)}
                            >
                                <div className="history-analysis__item-title">
                                    <span className="history-analysis__item-title-text">
                                        <FileTextOutlined className="history-analysis__item-icon" />
                                        {item.title || '未命名研报'}
                                    </span>
                                    <div className="history-analysis__item-title-time">
                                        {item.published_date || '—'}
                                    </div>
                                </div>
                                <div className="history-analysis__item-meta">
                                    <Tag variant="filled" className="is-accent text-10">研报</Tag>
                                    <span className="history-analysis__item-toggle">
                                        {isOpen ? '收起 ▲' : '查看详情 ▼'}
                                    </span>
                                </div>
                            </div>
                            {isOpen && (
                                <div className="history-analysis__item-detail">
                                    {detailLoading ? (
                                        <div className="history-analysis__item-detail-loading">
                                            <Spin size="small" />
                                        </div>
                                    ) : detail?.md_content ? (
                                        <div className="history-analysis__item-description">
                                            <MdViewer content={detail.md_content} />
                                        </div>
                                    ) : (
                                        <div className="history-analysis__item-description is-empty">
                                            暂无正文内容
                                            {downloadUrl ? '，可下载原文查看' : ''}
                                        </div>
                                    )}
                                    {downloadUrl && (
                                        <Button
                                            icon={<DownloadOutlined />}
                                            type="primary"
                                            size="small"
                                            className="text-10"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                handleDownload(downloadUrl)
                                            }}
                                        >
                                            下载原文
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
